/**
 * Unified Service
 * 
 * This service handles API calls that can be used by both admin and committee users.
 * It uses the privateApiClient for authenticated requests.
 * The backend will handle permissions based on the user's role.
 */

import { privateApiClient, handleApiError } from '../apiClient';
import API from '../../config/api';

/**
 * Get dashboard data for the current user (admin or committee)
 * @returns {Promise<Object>} Dashboard data including stats and candidates
 */
export const getDashboardData = async () => {
  try {
    // Use the standard dashboard endpoint - backend will filter based on user role
    const response = await privateApiClient.get(API.dashboard.stats);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch dashboard data');
  }
};

/**
 * Submit auto-rejection due to unmet requirements
 * @param {number|string} nominationId
 * @param {string} comments optional override message
 * @returns {Promise<Object>} Evaluation response
 */
export const rejectEvaluationDueToUnmetRequirements = async (nominationId, evaluationData = {}, comments) => {
  try {
    const response = await privateApiClient.post(
      API.evaluations.rejectUnmet(nominationId),
      evaluationData,
      { params: { comments } }
    );
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to reject due to unmet requirements');
  }
};

/**
 * Get candidates/nominations list
 * @param {Object} filters - Optional filters for candidates
 * @returns {Promise<Array>} List of candidates/nominations
 */
export const getCandidates = async (filters = {}) => {
  try {
    // The backend will filter nominations based on the user's role and permissions
    const response = await privateApiClient.get(API.nominations.base, { params: filters });
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch candidates');
  }
};

/**
 * Get candidate/nomination details by ID
 * @param {string} candidateId - Candidate/Nomination ID
 * @returns {Promise<Object>} Candidate/Nomination details
 */
export const getCandidateById = async (candidateId) => {
  try {
    // With our updated backend, committee members can now directly access nominations
    // from centers within their branch, so we don't need the fallback logic anymore
    const response = await privateApiClient.get(API.nominations.getById(candidateId));
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch candidate details');
  }
};

/**
 * Submit candidate evaluation
 * @param {Object} evaluationData - Evaluation data
 * @returns {Promise<Object>} Submitted evaluation
 */
export const submitEvaluation = async (evaluationData) => {
  try {
    const response = await privateApiClient.post(API.evaluations.create, evaluationData);
    return response.data;
  } catch (error) {
    // Special handling for 403 errors related to branch/center permissions
    if (error.response && error.response.status === 403) {
      const errorDetail = error.response.data?.detail || '';
      if (errorDetail.includes('Committee members can only evaluate')) {
        throw {
          message: 'لا يمكن تقييم هذا المرشح لأنه لا ينتمي إلى فرع اللجنة المخصصة لك',
          statusCode: 403,
          originalError: error
        };
      }
    }
    return handleApiError(error, 'Failed to submit evaluation');
  }
};

/**
 * Update candidate evaluation
 * @param {string} evaluationId - Evaluation ID
 * @param {Object} evaluationData - Updated evaluation data
 * @returns {Promise<Object>} Updated evaluation
 */
export const updateEvaluation = async (evaluationId, evaluationData) => {
  try {
    const response = await privateApiClient.put(API.evaluations.update(evaluationId), evaluationData);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to update evaluation');
  }
};

/**
 * Get evaluations for a nomination
 * @param {string} nominationId - Nomination ID
 * @returns {Promise<Array>} List of evaluations
 */
export const getNominationEvaluations = async (nominationId) => {
  try {
    // Use the committee evaluations endpoint which is already configured correctly
    const response = await privateApiClient.get(API.committee.evaluations.getByNomination(nominationId));
    return response.data;
  } catch (error) {
    console.error('Error fetching evaluations:', error);
    return handleApiError(error, 'Failed to fetch nomination evaluations');
  }
};

/**
 * Get all evaluations for the current committee member
 * @returns {Promise<Array>} List of all evaluations
 */
export const getAllEvaluations = async () => {
  try {
    // Use the evaluations base endpoint - backend will filter based on user role
    const response = await privateApiClient.get(API.evaluations.base);
    return response.data;
  } catch (error) {
    console.error('Error fetching all evaluations:', error);
    return handleApiError(error, 'Failed to fetch evaluations');
  }
};

/**
 * Download candidate profile as PDF
 * @param {string} candidateId - Candidate ID
 * @returns {Promise<Blob>} PDF file as blob
 */
export const downloadCandidateProfile = async (candidateId) => {
  try {
    const response = await privateApiClient.get(
      API.profiles.pdf(candidateId), 
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to download candidate profile');
  }
};

// Export all functions as a single object
const unifiedService = {
  getDashboardData,
  getCandidates,
  getCandidateById,
  submitEvaluation,
  updateEvaluation,
  getNominationEvaluations,
  getAllEvaluations,
  downloadCandidateProfile,
  rejectEvaluationDueToUnmetRequirements,
};

export default unifiedService;
